import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import EntityVM from './EntityVM';
import { observer } from 'mobx-react';
import * as CatalogueRoles from "../../../Models/Security/CatalogueRoles"
import DocumentTypeCatalogueEntry from '../General/DocumentTypeCatalogueEntry';
import { Catalogue } from '@singularsystems/neo-react-services';
import { EnumHelper, NotifyUtils, Validation } from '@singularsystems/neo-core';
import { TextAlignment } from '../../../Models/Catalogue/TextAlignment';
import { NotificationDuration } from '../../../../App/Models/NotificationDuration';
import AsyncImageComponent from '../../../Components/AsyncImage';
import BankDetails from '../../Components/BankDetails';

@observer
export default class EntityView extends Views.ViewBase<EntityVM> {

    constructor(props: unknown) {
        super("Entity", EntityVM, props);
    }

    public render() {
        const canEdit = this.viewModel.authorisationService.hasRole(CatalogueRoles.Entities.Edit);

        return (
            <div className="mt-4 mb-4">
                <Neo.Card title="Edit Entities">

                    {!this.viewModel.editingEntity &&
                        <div>
                            <div className="d-flex justify-content-between mb-4">
                                <Neo.Button icon="arrow-left" variant="light" onClick={() => history.back()}>Back</Neo.Button>
                            </div>
                            <NeoGrid.Grid items={this.viewModel.entityList} addButton={{ onClick: () => this.viewModel.newEntity() }}>
                                {(item, meta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column display={meta.entityName} />
                                        <NeoGrid.Column display={meta.registrationNo} />
                                        <NeoGrid.ButtonColumn editButton={{ onClick: () => this.viewModel.editEntities(item) }}
                                            deleteButton={{ onClick: () => this.viewModel.deleteEntities(item), disabled: !canEdit }} />
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>
                        </div>
                    }

                    {this.viewModel.editingEntity &&
                        <Neo.Form
                            allReadOnly={!canEdit}
                            model={this.viewModel.editingEntity}
                            validationDisplayMode={Validation.DisplayMode.Always}
                            showSummaryModal onSubmit={() => this.viewModel.saveEntities()}>
                            {(item, meta) => (
                                <div>
                                    <div className="d-flex justify-content-between mb-4">
                                        <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewModel.editingEntity = null}>Back</Neo.Button>
                                        <Neo.Button
                                            isSubmit icon="save"
                                            disabled={!this.viewModel.editingEntity!.readyToSave}
                                            pulse={this.viewModel.editingEntity!.readyToSave}>Save</Neo.Button>
                                    </div>

                                    <Neo.Card title="Details">
                                        <Neo.GridLayout>
                                            <div>
                                                <Neo.FormGroupInline bind={meta.entityName} />
                                                <Neo.FormGroupInline bind={meta.entityShortName} 
                                                    footerText="Used in 'From Account Description' field in the payment file"/>
                                                <Neo.FormGroupInline bind={meta.registrationNo} />
                                                <Neo.FormGroupInline bind={meta.companyName} />
                                                <Neo.FormGroupInline bind={meta.hasVestingTrust} />
                                                <Neo.FormGroupInline bind={meta.investorsCompleteDividendCycle}
                                                    footerText="Indicates that the final dividend date for investor investments should be according to the dividend cycle, rather than the investment end date." />
                                                <Neo.FormGroupInline bind={meta.isHDI} />
                                                <Neo.FormGroupInline bind={meta.hdiRefNo} />
                                                <Neo.FormGroupInline bind={meta.trustDeedFileId} fileProps={{ 
                                                    uploadEndpoint: this.viewModel.documentApiClient.getUploadDocumentUrl(),
                                                    downloadEndpointAsync: this.viewModel.documentApiClient.getFileDownloadUrl,
                                                    afterUpload: () => this.afterDocumentUpload() }} />
                                                <Neo.FormGroupInline bind={meta.minimumTransactionAmount} />
                                            </div>
                                            <div>
                                                <Neo.FormGroupInline bind={meta.friendlyName} />
                                                <AsyncImageComponent fileDescriptorId={item.reportHeaderImageId} maxHeight="100px" className="mb-3 text-center" />
                                                <Neo.FormGroupInline bind={meta.reportHeaderImageId} fileProps={{ uploadEndpoint: this.viewModel.imagesApiClient.getUploadImageUrl(), afterUpload: () => this.afterImageUpload() }} />
                                                <Neo.FormGroupInline bind={meta.reportHeaderAlignment} select={{ items: EnumHelper.asList(TextAlignment) }} />
                                                <Neo.FormGroupInline bind={meta.reportHeaderText} input={{ rows: 3 }} />
                                                <Neo.FormGroupInline bind={meta.reportDirectors} input={{ rows: 3 }} />
                                                <Neo.FormGroupInline bind={meta.reportDisclaimer} input={{ rows: 3 }} />
                                            </div>
                                        </Neo.GridLayout>
                                    </Neo.Card>
                                    <Neo.GridLayout className="mt-4">
                                        <Neo.Card title="Bank Details">
                                            <Neo.Button className="mb-3" variant="secondary" size="sm" onClick={() => this.viewModel.toggleBankDetails()}>
                                                {item.bankDetails == null ? 'Add Bank Details' : 'Remove Bank Details'}
                                            </Neo.Button>

                                            {item.bankDetails &&
                                                <div>
                                                    <BankDetails bankDetail={item.bankDetails} />
                                                </div>
                                            }
                                        </Neo.Card>

                                        <Neo.Card title="Documents">
                                            <h5>Investor Investment Documents</h5>
                                            <NeoGrid.Grid items={item.entityInvestorInvestmentDocuments} addButton={{ text: "Add Document Type", size: "sm" }} >
                                                {(item, meta) => (
                                                    <NeoGrid.Row>
                                                        <Catalogue.Column bind={meta.documentTypeId} catalogueType={DocumentTypeCatalogueEntry} />
                                                        <NeoGrid.Column bind={meta.isMandatory} />
                                                        <NeoGrid.ButtonColumn showDelete />
                                                    </NeoGrid.Row>
                                                )}
                                            </NeoGrid.Grid>

                                            <h5 className="mt-4">Issuer Investment Documents</h5>
                                            <NeoGrid.Grid items={item.entityIssuerInvestmentDocuments} addButton={{ text: "Add Document Type", size: "sm" }}>
                                                {(item, meta) => (
                                                    <NeoGrid.Row>
                                                        <Catalogue.Column bind={meta.documentTypeId} catalogueType={DocumentTypeCatalogueEntry} />
                                                        <NeoGrid.Column bind={meta.isMandatory} />
                                                        <NeoGrid.ButtonColumn showDelete />
                                                    </NeoGrid.Row>
                                                )}
                                            </NeoGrid.Grid>
                                        </Neo.Card>
                                    </Neo.GridLayout>
                                </div>
                            )}

                        </Neo.Form>
                    }
                </Neo.Card>
            </div>
        );
    }

    private afterImageUpload() {
        NotifyUtils.addSuccess("Image uploaded", "Click save to link the image to the entity.", NotificationDuration.Long);
    }

    private afterDocumentUpload() {
        NotifyUtils.addSuccess("Document uploaded", "Click save to link the document to the entity.", NotificationDuration.Long);
    }

    public onLeave(): string | undefined {
        if (this.viewModel.editingEntity?.isDirty) {
            return "Are you sure you want to leave this page without saving?";
        }
        return undefined;
    }
}